import { roundToNearest } from '@shared/utils/math';
import moment from 'moment';
/**
 * Date utils
 *
 * @author Dec Norton <decnorton@gmail.com>
 */
import { pad } from './strings';

export const DATE_SHORT_FORMAT = 'll';
export const DATE_FORMAT = 'ddd, ll';
export const TIMESTAMP_FORMAT = 'HH:mm, Do MMM YYYY';
export const TIMESTAMP_SAME_YEAR_FORMAT = 'HH:mm, Do MMM';
export const TIME_FORMAT = 'HH:mm';
export const SQL_FORMAT_DATE = 'YYYY-MM-DD';
export const SQL_FORMAT_TIME = 'HH:mm:ss';
export const SQL_FORMAT_DATETIME = 'YYYY-MM-DD HH:mm:ss';

/**
 * Whether the current locale uses US-style date formatting (MM/DD/YYYY).
 */
export function isUsLocale(): boolean {
    const locale = (window.CareLineLive?.js_locale || '').toLowerCase();

    return locale.startsWith('en-us');
}

/**
 * Returns the short date format string for the current locale.
 * US: MM/DD/YYYY, all others: DD/MM/YYYY
 */
export function shortDateFormat(): string {
    return isUsLocale() ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
}

/**
 * Returns the date parse formats for the current locale.
 */
export function dateParseFormats(): string[] {
    if (isUsLocale()) {
        return ['YYYY-MM-DD', 'MM-DD-YYYY', 'MM/DD/YYYY'];
    }

    return ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY'];
}

/**
 * Returns the first day of the week for the current locale.
 * US: Sunday (0), all others: Monday (1)
 */
export function firstDayOfWeek(): number {
    return isUsLocale() ? 0 : 1;
}

export const EPOCH = moment(0);

/**
 * @param {moment.Moment} instance
 * @return {Number}
 */
export function weeksSinceEpoch(instance) {
    return moment(instance).diff(EPOCH, 'weeks');
}

/**
 * Format the given moment in a readable format.
 */
export function dateReadable(dateMoment, todayMoment = moment().startOf('day')): string {
    if (!dateMoment) {
        return;
    }

    if (!moment.isMoment(dateMoment)) {
        dateMoment = moment(dateMoment);
    }

    if (!moment.isMoment(todayMoment)) {
        todayMoment = moment(todayMoment);
    }

    let format = 'ddd, D MMM';

    if (!dateMoment.isSame(todayMoment, 'year')) {
        format += ' YYYY';
    }

    const formatted = dateMoment.clone().local().format(format);

    const diffDays = dateMoment.clone().startOf('day').diff(todayMoment, 'days');

    switch (diffDays) {
        case -1:
            return `Yesterday (${formatted})`;

        case 0:
            return `Today (${formatted})`;

        case 1:
            return `Tomorrow (${formatted})`;
    }

    return formatted;
}

export function readableDateRange(start, finish) {
    if (!moment.isMoment(start)) {
        start = moment(start);
    }

    if (!moment.isMoment(finish)) {
        finish = finish ? moment(finish) : undefined;
    }

    if (!finish || start.isSame(finish, 'day')) {
        return start.format(DATE_FORMAT);
    }

    let startFormatString = 'D';

    if (!start.isSame(finish, 'month')) {
        startFormatString += ' MMM';
    }

    if (!start.isSame(finish, 'year')) {
        startFormatString += ' YY';
    }

    const startFormatted = start.isValid() ? moment(start).format(startFormatString) : '';
    const finishFormatted = finish.isValid() ? moment(finish).format(DATE_SHORT_FORMAT) : '';

    return `${startFormatted} to ${finishFormatted}`;
}

/**
 * Transform the given decimal representation of hours into seconds.
 * @param {string|number} value
 * @returns {number}
 */
export function decimalHoursToSeconds(value) {
    value = parseFloat(value);

    if (isNaN(value)) {
        return null;
    }

    return Math.floor(value * 3600);
}

/**
 * Takes a decimal and returns a time string. E.g. 1.5 -> 1:30
 * @param {number} input
 * @param {number} roundToMinutes
 * @return {string}
 */
export function decimalHoursToHoursMinutes(input: number, roundToMinutes = 0) {
    let hours = Math.floor(input);
    let minutes = (input % 1) * 60;

    if (roundToMinutes) {
        minutes = Math.min(60, roundToNearest(minutes, roundToMinutes));
    }

    if (minutes === 60) {
        hours += 1;
        minutes = 0;
    }

    return `${pad(hours, 2)}:${pad(minutes, 2)}`;
}

/**
 * Check whether the specified event falls on the given date.
 * @param {moment.Moment} date
 * @param {moment.Moment} eventStart
 * @param {moment.Moment} eventFinish
 * @returns {boolean}
 */
export function fallsOnDate(date, eventStart, eventFinish) {
    const dateStartMillis = date.clone().startOf('day').valueOf();

    const dateFinishMillis = date.clone().endOf('day').valueOf();

    const startMillis = eventStart.valueOf();
    const finishMillis = eventFinish.valueOf();

    return intercepts([dateStartMillis, dateFinishMillis], [startMillis, finishMillis]);
}

/**
 * Takes two arrays containing the start and finish times in seconds.
 *
 * @param {Array} first
 * @param {Array} second
 * @param {boolean} allowEqualTo
 * @returns {boolean}
 */
export function intercepts(first, second, allowEqualTo = false) {
    return allowEqualTo
        ? !(second[1] <= first[0] || second[0] >= first[1])
        : !(second[1] < first[0] || second[0] > first[1]);
}

/**
 * Check if the given value is a date (without time).
 * @param value
 * @returns {boolean}
 */
export function isDate(value) {
    return /^(19|20)\d\d[- /.](0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])$/.test(value);
}

/**
 *
 * @param {Moment} value
 */
export function isToday(value) {
    const valueMoment = moment(value);

    if (!valueMoment.isValid()) {
        return false;
    }

    return moment().isSame(valueMoment, 'day');
}

/**
 * Converts HH:mm to a decimal representation of hours.
 * @param  {string} value
 * @return {Number}
 */
export function hoursMinutesToDecimal(value) {
    const seconds = hoursMinutesToSeconds(value);

    return seconds !== null ? seconds / 3600 : null;
}

/**
 * Transform the given HH:mm string into seconds.
 * @param value
 * @returns {*}
 */
export function hoursMinutesToSeconds(value) {
    if (value.indexOf(':') === -1) {
        return null;
    }

    const parts = value.split(':');
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;

    return Math.floor(hours * 3600 + minutes * 60);
}

/**
 * Whether the two dates are the same day.
 * @param {moment.Moment|Date} a
 * @param {moment.Moment|Date} b
 * @returns {boolean}
 */
export function isSameDay(a, b) {
    if (!a || !b) {
        return false;
    }

    // Ensure they are both local
    return moment(a).local().isSame(moment(b).local(), 'day');
}

/**
 * Convert minutes into a readable string.
 * @param {Number} value
 * @returns {string}
 */
export function minutesToReadable(value) {
    if (value === null) {
        return null;
    }

    return secondsToReadable(value * 60);
}

/**
 * Convert minutes into a readable string.
 * @param {Number} value
 * @returns {string}
 */
export function minutesToShortReadable(value) {
    return secondsToShortReadable(value * 60);
}

/**
 * Convert seconds into a readable string.
 * @param {Number} value
 * @returns {string}
 */
export function secondsToHoursMinutes(value) {
    value = parseInt(value);

    // Value should be in seconds
    const hours = Math.floor(value / 3600);
    value %= 3600;
    const minutes = Math.floor(value / 60);

    return `${hours}:${pad(minutes)}`;
}

/**
 * Convert seconds into a readable string.
 * @param {Number} value
 * @returns {string}
 */
export function secondsToReadable(value) {
    value = parseInt(value);

    if (isNaN(value)) {
        return '0m';
    }

    const isNegative = value < 0;

    value = Math.abs(value);

    // Value should be in seconds
    const hours = Math.floor(value / 3600);
    value %= 3600;
    const minutes = Math.floor(value / 60);
    value %= 60;
    const seconds = value;

    const parts = [];

    if (hours > 0) {
        parts.push(`${hours}h`);

        if (minutes > 0) {
            parts.push(`${minutes}m`);
        }
    } else if (minutes > 0) {
        parts.push(`${minutes}m`);
        if (seconds > 0) {
            parts.push(`${seconds}s`);
        }
    } else {
        parts.push(`${seconds}s`);
    }

    return (isNegative ? '-' : '') + parts.join(' ');
}

/**
 * Convert seconds into a readable string.
 * @param {Number} value
 * @returns {string}
 */
export function secondsToShortReadable(value) {
    value = parseInt(value);

    if (!value) {
        return '0m';
    }

    // Value should be in seconds
    const hours = Math.floor(value / 3600);
    value %= 3600;
    const minutes = Math.floor(value / 60);

    const parts = [];

    if (hours > 0) {
        parts.push(`${hours}h`);

        if (minutes > 0) {
            parts.push(`${pad(minutes, 2)}m`);
        }
    } else if (minutes > 0) {
        parts.push(`${minutes}m`);
    }

    return parts.join(' ');
}

export function toSqlTime(value, convertToUtc = false) {
    if (!value) {
        return value;
    }

    if (!moment.isMoment(value)) {
        value = moment(value);
    }

    if (convertToUtc) {
        value = value.utc();
    }

    return value.format(SQL_FORMAT_TIME);
}

export function toSqlDate(value, convertToUtc = true) {
    if (!value) {
        return value;
    }

    if (!moment.isMoment(value)) {
        value = moment(value);
    }

    if (convertToUtc) {
        value = value.utc();
    }

    return value.format(SQL_FORMAT_DATE);
}

export function toSqlDateTime(value, convertToUtc = true) {
    if (!value) {
        return value;
    }

    if (!moment.isMoment(value)) {
        value = moment(value);
    }

    if (convertToUtc) {
        value = value.utc();
    }

    return value.format(SQL_FORMAT_DATETIME);
}

export function toIso8601(value, convertToUtc = true) {
    if (!value) {
        return value;
    }

    if (!moment.isMoment(value)) {
        value = moment(value);
    }

    if (convertToUtc) {
        value = value.utc();
    }

    return value.toISOString();
}

/**
 *
 * @param {moment.Moment} date
 * @param {moment.Moment?} reference
 * @return {string}
 */
export function dayRelative(date, reference = moment().local().startOf('day')) {
    const diffDays = date.clone().local().startOf('day').diff(reference, 'days');

    switch (diffDays) {
        case -1:
            return 'Yesterday';
        case 0:
            return 'Today';
        case 1:
            return 'Tomorrow';
    }

    return '';
}

/**
 * @param {moment.Moment} instance
 * @param {int} minutes
 * @param {string} direction
 */
export function roundToMinutes(instance, minutes = 1, direction = 'round') {
    const precision = minutes * 60;

    let seconds = instance.toDate().getTime() / 1000;

    seconds = Math[direction](seconds / precision) * precision;

    return moment(new Date(seconds * 1000));
}

export const toSqlFormat = toSqlDateTime;
